import { useEffect, useRef } from 'react'
import { Palette } from 'lucide-react'

interface WhiteboardViewerProps {
  canvasElements?: {
    canvasData?: string
  }
  content?: string
  whiteboardTextDiscussion?: string
  strokeData?: any
}

export default function WhiteboardViewer({ 
  canvasElements, 
  content, 
  whiteboardTextDiscussion,
  strokeData 
}: WhiteboardViewerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  // Restore canvas drawing from saved data
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Set canvas size
    canvas.width = canvas.offsetWidth
    canvas.height = canvas.offsetHeight

    if (!canvas.width || canvas.width < 10) {
      canvas.width = 960
    }
    if (!canvas.height || canvas.height < 10) {
      canvas.height = 540
    }

    // Set white background
    ctx.fillStyle = 'white'
    ctx.fillRect(0, 0, canvas.width, canvas.height)

    // Try to restore from different data sources
    let imageDataSrc = null

    // Priority 1: canvasElements.canvasData (base64 image)
    if (canvasElements?.canvasData) {
      imageDataSrc = canvasElements.canvasData
    }
    // Priority 2: strokeData.canvasData
    else if (strokeData?.canvasData) {
      imageDataSrc = strokeData.canvasData
    }
    // Priority 3: Any other canvas data structure
    else if (typeof strokeData === 'string' && strokeData.startsWith('data:image')) {
      imageDataSrc = strokeData
    }

    // Restore the canvas image
    if (imageDataSrc) {
      const image = new Image()
      image.onload = () => {
        // Clear and set background first
        ctx.fillStyle = 'white'
        ctx.fillRect(0, 0, canvas.width, canvas.height)
        
        // Draw the saved image
        ctx.drawImage(image, 0, 0, canvas.width, canvas.height)
      }
      image.onerror = () => {
        console.warn('Failed to load whiteboard canvas image')
      }
      image.src = imageDataSrc
    }
  }, [canvasElements, strokeData])

  const hasVisualContent = canvasElements?.canvasData || strokeData?.canvasData || 
    (typeof strokeData === 'string' && strokeData.startsWith('data:image'))

  return (
    <div className="space-y-6">
      {/* Canvas Drawing */}
      {hasVisualContent && (
        <div>
          <h5 className="text-sm font-medium text-gray-700 mb-3 flex items-center space-x-2">
            <Palette className="w-4 h-4" />
            <span>Whiteboard Drawing</span>
          </h5>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <canvas
              ref={canvasRef}
              className="w-full border-2 border-gray-200 rounded-lg bg-white"
              style={{ minHeight: '400px', height: '500px' }}
            />
          </div>
        </div>
      )}

      {/* Content Notes */}
      {content && (
        <div>
          <h5 className="text-sm font-medium text-gray-700 mb-3">
            Content Notes
          </h5>
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">
              {content}
            </pre>
          </div>
        </div>
      )}

      {/* Discussion Notes */}
      {whiteboardTextDiscussion && (
        <div>
          <h5 className="text-sm font-medium text-gray-700 mb-3">
            Discussion Notes
          </h5>
          <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
            <p className="text-orange-900 text-sm whitespace-pre-wrap leading-relaxed">
              {whiteboardTextDiscussion}
            </p>
          </div>
        </div>
      )}

      {/* Debug information */}
      {!hasVisualContent && !content && !whiteboardTextDiscussion && (
        <div className="text-center py-8">
          <div className="bg-gray-50 rounded-lg p-8 max-w-md mx-auto">
            <Palette className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Whiteboard Content</h3>
            <p className="text-gray-600">No whiteboard drawings or notes were found for this session.</p>
          </div>
        </div>
      )}
    </div>
  )
}
